import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import Navbar from "../../feature/Navbar";
import Footer from "../../feature/Footer";
import WhatsAppButton from "../../feature/WhatsAppButton";
import { ProductCard } from "../../components/base/ProductCard";

type ViewMode = "grid" | "list";

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const selectedCategory = searchParams.get("categoria") || "";
  const searchTerm = searchParams.get("busca") || "";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          { data: productsData, error: productsError },
          { data: categoriesData, error: categoriesError },
        ] = await Promise.all([
          supabase
            .from("ray_melo_products")
            .select("*")
            .order("created_at", { ascending: false }),
          supabase
            .from("categories")
            .select("*")
            .order("created_at", { ascending: true }),
        ]);
        if (productsError) throw productsError;
        if (categoriesError) throw categoriesError;
        setProducts(productsData || []);
        setCategories(categoriesData || []);
      } catch (error) {
        console.error("Erro ao carregar catálogo:", error);
        setProducts([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

 const filteredProducts = useMemo(() => {
  return (products || []).filter((product) => {
    const v = product.visible;

    // 🔒 FILTRO REALMENTE SEGURO
    if (!(v === true || v === 1 || v === "true" || v === "1")) {
      return false;
    }

    const matchesCategory =
      !selectedCategory ||
      (Array.isArray(product.categories) &&
        product.categories.includes(selectedCategory));

    const matchesSearch =
      !searchTerm ||
      String(product.name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });
}, [products, selectedCategory, searchTerm]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  if (loading) {
    return (
      <div
        className="min-h-screen bg-white"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-black/20 border-t-black rounded-full animate-spin" />
            <p className="text-[11px] tracking-[0.2em] text-black/40 uppercase">
              Carregando...
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-white text-black"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      <Navbar />

      {/* Hero header */}
      <div className="relative bg-[#1A1A1A] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=elegant%20Brazilian%20fashion%20boutique%20collection%20flat%20lay%20clothing%20arrangement%2C%20dark%20moody%20background%2C%20editorial%20fashion%20photography%2C%20sophisticated%20minimalist%20style%2C%20warm%20tones&width=1440&height=220&seq=catalog-hero&orientation=landscape"
          alt="Catálogo"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="flex items-center gap-2 text-[11px] text-white/50 mb-4">
            <Link to="/" className="hover:text-white transition-colors">
              Início
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-white/80">Catálogo</span>
          </div>
          <h1
            className="text-4xl md:text-5xl font-light text-white tracking-[0.05em]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Catálogo Completo
          </h1>
          <p className="text-[12px] text-white/50 mt-2 tracking-wide">
            {filteredProducts.length}{" "}
            {filteredProducts.length === 1
              ? "peça encontrada"
              : "peças encontradas"}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10">
        <div className="flex gap-8">

          {/* Sidebar — desktop */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-24">
              {/* Search */}
              <div className="mb-8">
                <p className="text-[10px] tracking-[0.25em] font-semibold text-black uppercase mb-3">
                  Buscar
                </p>
                <div className="flex items-center border border-black/15 px-3 gap-2 focus-within:border-black/40 transition-colors">
                  <i className="ri-search-line text-black/30 text-sm flex-shrink-0" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => updateFilter("busca", e.target.value)}
                    placeholder="Nome do produto..."
                    className="flex-1 h-9 bg-transparent text-[12px] text-black outline-none placeholder:text-black/25"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => updateFilter("busca", "")}
                      className="text-black/30 hover:text-black cursor-pointer"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  )}
                </div>
              </div>

              {/* Categories */}
              <div>
                <p className="text-[10px] tracking-[0.25em] font-semibold text-black uppercase mb-3">
                  Categorias
                </p>
                <ul className="space-y-0.5">
                  <li>
                    <button
                      type="button"
                      onClick={() => updateFilter("categoria", "")}
                      className={`w-full text-left px-3 py-2.5 text-[12px] tracking-[0.05em] transition-colors cursor-pointer flex items-center justify-between group ${
                        !selectedCategory
                          ? "bg-black text-white"
                          : "text-black/60 hover:text-black hover:bg-black/5"
                      }`}
                    >
                      <span>Todas as peças</span>
                      <span
                        className={`text-[10px] ${
                          !selectedCategory ? "text-white/60" : "text-black/30"
                        }`}
                      >
                        {products.length}
                      </span>
                    </button>
                  </li>
                  {categories.map((cat) => {
                    const count = products.filter(
                      (p) =>
                        Array.isArray(p.categories) &&
                        p.categories.includes(cat.slug)
                    ).length;
                    const active = selectedCategory === cat.slug;
                    return (
                      <li key={cat.id}>
                        <button
                          type="button"
                          onClick={() => updateFilter("categoria", cat.slug)}
                          className={`w-full text-left px-3 py-2.5 text-[12px] tracking-[0.05em] transition-colors cursor-pointer flex items-center justify-between ${
                            active
                              ? "bg-black text-white"
                              : "text-black/60 hover:text-black hover:bg-black/5"
                          }`}
                        >
                          <span>{cat.name}</span>
                          <span
                            className={`text-[10px] ${
                              active ? "text-white/60" : "text-black/30"
                            }`}
                          >
                            {count}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {(selectedCategory || searchTerm) && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 flex items-center gap-1.5 text-[11px] text-black/40 hover:text-black transition-colors cursor-pointer"
                  >
                    <i className="ri-refresh-line text-xs" />
                    Limpar filtros
                  </button>
                )}
              </div>
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-black/8">
              <div className="flex items-center gap-3">
                {/* Mobile filter toggle */}
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden flex items-center gap-2 border border-black/15 px-3 h-9 text-[11px] tracking-[0.1em] uppercase hover:border-black/40 transition-colors cursor-pointer"
                >
                  <i className="ri-equalizer-line text-sm" />
                  Filtros
                  {(selectedCategory || searchTerm) && (
                    <span className="w-4 h-4 bg-black text-white text-[9px] rounded-full flex items-center justify-center">
                      1
                    </span>
                  )}
                </button>

                {selectedCategory && (
                  <span className="hidden lg:flex items-center gap-1.5 bg-black/5 px-3 py-1.5 text-[11px] text-black/70">
                    {categories.find((c) => c.slug === selectedCategory)?.name}
                    <button
                      onClick={() => updateFilter("categoria", "")}
                      className="hover:text-black cursor-pointer ml-1"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </span>
                )}
              </div>

              {/* View toggle */}
              <div className="flex items-center gap-1 border border-black/10 p-0.5">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${
                    viewMode === "grid"
                      ? "bg-black text-white"
                      : "text-black/40 hover:text-black"
                  }`}
                  aria-label="Grade"
                >
                  <i className="ri-grid-line text-sm" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${
                    viewMode === "list"
                      ? "bg-black text-white"
                      : "text-black/40 hover:text-black"
                  }`}
                  aria-label="Lista"
                >
                  <i className="ri-list-unordered text-sm" />
                </button>
              </div>
            </div>

            {/* Products */}
            {filteredProducts.length === 0 ? (
              <div className="py-24 text-center">
                <i className="ri-search-line text-5xl text-black/15 mb-5 block" />
                <p className="text-black/40 uppercase text-[11px] tracking-[0.25em] mb-6">
                  Nenhuma peça encontrada
                </p>
                <button
                  onClick={clearFilters}
                  className="inline-block border border-black/25 px-8 py-3 text-[11px] tracking-[0.18em] uppercase hover:bg-black hover:text-white transition-all cursor-pointer whitespace-nowrap"
                >
                  Limpar filtros
                </button>
              </div>
            ) : viewMode === "grid" ? (
              <div
                className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5"
                data-product-shop
              >
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div
                className="flex flex-col divide-y divide-black/8"
                data-product-shop
              >
                {filteredProducts.map((product) => (
                  <ListProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/10">
              <p className="text-[12px] tracking-[0.2em] font-semibold uppercase">
                Filtros
              </p>
              <button
                onClick={() => setSidebarOpen(false)}
                className="w-8 h-8 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-5 py-6 space-y-8">
              {/* Search */}
              <div>
                <p className="text-[10px] tracking-[0.25em] font-semibold text-black uppercase mb-3">
                  Buscar
                </p>
                <div className="flex items-center border border-black/15 px-3 gap-2">
                  <i className="ri-search-line text-black/30 text-sm" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => updateFilter("busca", e.target.value)}
                    placeholder="Nome do produto..."
                    className="flex-1 h-9 bg-transparent text-[12px] outline-none placeholder:text-black/25"
                  />
                </div>
              </div>
              {/* Categories */}
              <div>
                <p className="text-[10px] tracking-[0.25em] font-semibold text-black uppercase mb-3">
                  Categorias
                </p>
                <ul className="space-y-0.5">
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        updateFilter("categoria", "");
                        setSidebarOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-[12px] transition-colors cursor-pointer ${
                        !selectedCategory
                          ? "bg-black text-white"
                          : "text-black/60 hover:bg-black/5"
                      }`}
                    >
                      Todas as peças
                    </button>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <button
                        type="button"
                        onClick={() => {
                          updateFilter("categoria", cat.slug);
                          setSidebarOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2.5 text-[12px] transition-colors cursor-pointer ${
                          selectedCategory === cat.slug
                            ? "bg-black text-white"
                            : "text-black/60 hover:bg-black/5"
                        }`}
                      >
                        {cat.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <WhatsAppButton />
    </div>
  );
}

// List view card
function ListProductCard({ product }: { product: any }) {
  const WA_NUMBER = "5511923169450";

  const getImage = (p: any): string => {
    if (typeof p?.image_url === "string" && p.image_url.trim())
      return p.image_url;
    if (typeof p?.image === "string" && p.image.trim()) return p.image;
    if (Array.isArray(p?.images)) {
      const first = p.images.find(
        (img: any) => typeof img === "string" && img.trim()
      );
      if (first) return first;
    }
    return "";
  };

  const getSlug = (p: any): string => {
    if (typeof p?.slug === "string" && p.slug.trim()) return p.slug;
    return String(p?.id || "");
  };

  const image = getImage(product);
  const slug = getSlug(product);
  const waMessage = encodeURIComponent(
    `Olá! Tenho interesse na peça: ${product?.name || "Produto"}`
  );
  const waHref = `https://wa.me/${WA_NUMBER}?text=${waMessage}`;

  return (
    <div className="flex gap-5 py-5 group">
      <Link
        to={`/produto/${slug}`}
        className="flex-shrink-0 overflow-hidden bg-[#F5F2EC] w-28 h-36"
      >
        {image ? (
          <img
            src={image}
            alt={product?.name}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-[#F5F2EC]" />
        )}
      </Link>
      <div className="flex flex-col justify-between py-1 flex-1 min-w-0">
        <div>
          <Link to={`/produto/${slug}`}>
            <h3 className="text-[13px] font-medium text-black uppercase tracking-[0.05em] leading-snug hover:opacity-60 transition-opacity line-clamp-2">
              {product?.name || "Produto"}
            </h3>
          </Link>
          {product?.description && (
            <p className="text-[12px] text-black/45 mt-2 leading-relaxed line-clamp-2">
              {product.description}
            </p>
          )}
        </div>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-black/20 px-4 py-2 text-[11px] tracking-[0.12em] uppercase text-black hover:bg-black hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer w-fit mt-3"
        >
          <i className="ri-whatsapp-line text-xs" />
          Comprar
        </a>
      </div>
    </div>
  );
}